import React, { useEffect, useState } from 'react'

import Chart from './components/chart'
import Columns from 'react-bulma-components/lib/components/columns'
import GeoJsonZoomTo from './components/bngmap/GeoJsonZoomTo'
import Legend from './components/legend'
import Nav from './components/nav'
import BNGMap from './components/bngmap/BNGMap'
import Scenario from './components/scenario'
import { WMSTileLayer } from 'react-leaflet'

import './App.sass'

const urlBase = process.env.REACT_APP_API_PATH
const regions = require('./data/regions.json')

function calculateYranges (data, setYranges) {
  const yranges = {
    0: [0, 0],
    5: [0, 0],
    10: [0, 0],
    15: [0, 0],
    20: [0, 0],
    25: [0, 0],
    30: [0, 0]
  }
  const keys = Object.keys(data)
  const regex = /^lu(?:Ag|Gr|Na)_(\d{1,2})_af(?:Ag|Bi|Co|En|Na)_(\d{1,2})_cr(?:Div|Ext|Int|Na)_gr(?:Div|Ext|Int|Na)$/
  keys.forEach(key => {
    const [, lu, af] = key.match(regex)
    const expansion = Math.max(lu, af)

    data[key].forEach(variable => {
      if (variable['change'] > yranges[expansion][1]) {
        yranges[expansion][1] = variable['change']
      }

      if (variable['change'] < yranges[expansion][0]) {
        yranges[expansion][0] = variable['change']
      }
    })
  })
  setYranges(yranges)
}

export default function App ({
  afforestOptions,
  agriOptions,
  cropOptions,
  grassOptions,
  mapOptions,
  summaryOptions,
  variableOptions
}) {
  const [afforestation, setAfforestation] = useState({ id: afforestOptions[0].key })
  const [afforestationExpansion, setAfforestationExpansion] = useState(0)
  const [agricultural, setAgricultural] = useState({ id: agriOptions[0].key })
  const [agriculturalExpansion, setAgriculturalExpansion] = useState(0)
  const [crop, setCrop] = useState({ id: cropOptions[0].key })
  const [grassland, setGrassland] = useState({ id: grassOptions[0].key })
  const [map, setMap] = useState({ id: mapOptions[0].key })
  const [summary, setSummary] = useState({ id: summaryOptions[0].key, label: summaryOptions[0].label })
  const [data, setData] = useState({})
  const [yranges, setYranges] = useState({})

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${urlBase}/data/${summary.id}.json`, { headers: { accept: 'application/json' } })
        if (response.ok) setData(await response.json())
      } catch (ex) {
        setData({})
      }
    }
    fetchData()
  }, [summary])

  useEffect(() => calculateYranges(data, setYranges), [data])

  const ag = agriculturalExpansion === 0 ? 'Na' : agricultural.id
  const af = afforestationExpansion === 0 ? 'Na' : afforestation.id
  const selectData = data[`lu${ag}_${agriculturalExpansion}_af${af}_${afforestationExpansion}_cr${crop.id}_gr${grassland.id}`]
  const layer = `lu${ag}_${agriculturalExpansion}_af${af}_${afforestationExpansion}`

  const maps = {
    landcover: 'landcover',
    landcover_change: 'landcover_change',
    crop: `crop_cr${crop.id}`,
    grass: `grass_gr${grassland.id}`
  }
  const mapName = maps[map.id] || ''

  return <>
    <Nav />
    <main className='app'>
      <Columns>
        <Columns.Column size='one-third' className='map-container'>
          <BNGMap>
            <WMSTileLayer url={`${urlBase}/maps/mapserv?map=/background/land.map`} layers='land' format='image/png' transparent />
            <WMSTileLayer url={`${urlBase}/maps/mapserv?map=/maps/${mapName}.map`} layers={layer} format='image/png' transparent />
            <GeoJsonZoomTo data={regions} selectedId={summary.id} />
          </BNGMap>
          <Legend name={map.id} />
        </Columns.Column>
        <Columns.Column size='two-thirds'>
          <Chart values={selectData} title={summary.label} descriptions={variableOptions} yrange={yranges[Math.max(afforestationExpansion, agriculturalExpansion)]} />
        </Columns.Column>
      </Columns>
      <Columns>
        <Columns.Column>
          <Scenario
            title='Select Map Display'
            first={{
              title: 'Show On Map',
              info: 'Select what is shown on the map – land cover under the selected scenario, land cover change from the baseline scenario, the selected cropping scenario or the selected grassland management scenario',
              options: mapOptions,
              select: setMap,
              selected: map.id
            }}
            second={{
              title: 'Summarise By Region',
              info: 'Choose a region to summarise outputs by',
              options: summaryOptions,
              select: setSummary,
              selected: summary.parent || summary.id
            }}
          />
        </Columns.Column>
        <Columns.Column>
          <Scenario
            slider
            title='Select Land Cover Change Scenario'
            first={{
              title: 'Agricultural Land Cover Type',
              info: 'Select the scenario of change in agricultural land use, and the percentage change (using the slider). There are a choice of two scenarios, all derived from modifying the [CEH Land Cover Map 2007](https://www.ceh.ac.uk/services/land-cover-map-2007) using the [InVEST Scenario Generator tool](http://data.naturalcapitalproject.org/nightly-build/invest-users-guide/html/scenario_generator.html).',
              options: agriOptions,
              select: setAgricultural,
              selected: agricultural.id,
              value: agriculturalExpansion,
              handle: setAgriculturalExpansion
            }}
            second={{
              title: 'Afforestation Type',
              info: 'Select the scenario of change in forest area, and the percentage change (using the slider). There are a choice of four scenarios, all derived from modifying the [CEH Land Cover Map 2007](https://www.ceh.ac.uk/services/land-cover-map-2007) using the [InVEST Scenario Generator tool](http://data.naturalcapitalproject.org/nightly-build/invest-users-guide/html/scenario_generator.html) based on potential outcomes from socioeconomic pressures.',
              options: afforestOptions,
              select: setAfforestation,
              selected: afforestation.id,
              value: afforestationExpansion,
              handle: setAfforestationExpansion
            }} />
        </Columns.Column>
        <Columns.Column>
          <Scenario
            title='Select Crop Grassland Scenario'
            first={{
              title: 'Crops',
              info: 'Select a cropping pattern. There is a choice of four scenarios, all derived by modifying the [CEH Land Cover plus: Crops map 2016](https://www.ceh.ac.uk/crops2015) using the [InVEST Scenario Generator tool](http://data.naturalcapitalproject.org/nightly-build/invest-users-guide/html/scenario_generator.html) and the constraints of the [agricultural land classification](http://publications.naturalengland.org.uk/category/5954148537204736), based on potential outcomes from socioeconomic pressures.',
              options: cropOptions,
              select: setCrop,
              selected: crop.id
            }}
            second={{
              title: 'Grassland',
              info: 'Select a grassland management pattern. There is a choice of four scenarios, all derived by modifying the stocking rate of improved and semi-natural grasslands, based on potential outcomes from socioeconomic pressures.',
              options: grassOptions,
              select: setGrassland,
              selected: grassland.id
            }}
          />
        </Columns.Column>

      </Columns>
    </main>
  </>
}
